import LoqateAddressSearch, {
  type Address as LoqateAddress,
} from "react-loqate";
import { LOQATE_API_KEY } from "@/config";
import {
  AddressAutocompleteErrorBoundary,
  type AddressSearchFailure,
} from "./AddressAutocompleteErrorBoundary";
import { AddressSuggestion } from "./AddressSuggestion";
import { Input, InputProps } from "@/components/ui/Input";
import { Alert } from "@/components/ui/Alert";
import classes from "./AddressAutocomplete.module.scss";
import type {
  AddressAutocompleteProps,
  AddressAutoCompleteScreens,
} from "./AddressAutocompleteProps";
import { countryNames } from "@/utils/countries";
import { FormControl } from "@/components/ui/FormControl";
import { Field } from "@/components/Field";
import * as z from "zod";
import { AddressFields } from "@/types/Address";
import mergeRefs from "merge-refs";
import { createContext, useContext, useEffect, useState } from "react";
import { useDonationFormStore } from "@/stores";
import { useTranslation } from "@/hooks/useTranslation";
import { Select } from "@/components/ui/Select";
import { InputAdornment } from "@/components/ui/InputAdornment";
import { Search } from "@/components/ui/Icon/Search";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useValidationGroup } from "@/hooks/useValidationGroup";
import { getValueByPath } from "@/utils/getValueByPath";
import {
  isUkCountry,
  isValidPostcodeForCountry,
  normalizePostcodeForCountry,
} from "@/utils/uk-postcode";

const LOCALE = "en_GB";
const CONTRIES = ["GBR"];

/**
 * By default react-loqate renders its suggestion list through a portal into
 * `document.body`. React then owns a node that lives outside our tree, and any
 * script on the host page that touches `body` (cookie banners, A/B testing,
 * password managers) can detach it — after which unmounting the field throws
 * `NotFoundError: The object can not be found here` from `removeChild` and
 * takes the whole address step down with it.
 *
 * Rendering the list inline keeps it inside our own tree. The library still
 * passes the viewport coordinates it computed for the portal, which we drop in
 * favour of positioning the list against its container in CSS.
 *
 * `translate="no"` keeps Google Translate from rewriting the text nodes React
 * owns (DONATION-PLATFORM-FRONTEND-33). Addresses are proper names anyway.
 */
function InlineList({
  style: _portalPosition,
  ...props
}: React.ComponentProps<"ul">) {
  const t = useTranslation("_common");

  return (
    <ul // NOSONAR typescript:S6819: a native <select> cannot hold react-loqate's items
      {...props}
      role="listbox"
      aria-label={t("address-autocomplete-suggestions") as string}
      translate="no"
    />
  );
}

/**
 * Strips leading whitespace before the value reaches react-loqate.
 *
 * react-loqate only skips the request when its value is exactly `""`, so a
 * lone space — typed, autofilled, or added by a mobile keyboard — is sent to
 * Loqate as a `Text` with no `Container`. Loqate answers with a "Text or
 * Container Required" error item, which react-loqate re-throws during render
 * and which used to take the whole autocomplete down (DONATION-PLATFORM-7W,
 * DONATION-PLATFORM-7E).
 *
 * Only the leading side is trimmed: a trailing space is a donor still typing.
 */
function handleSearchChange(
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined,
) {
  return (event: React.ChangeEvent<HTMLInputElement>) => {
    const trimmed = event.target.value.trimStart();

    if (trimmed !== event.target.value) {
      event.target.value = trimmed;
    }

    onChange?.(event);
  };
}

// react-loqate owns the input props. Context supplies the validation ref without
// replacing its Input component type on each render and detaching the focus target.
const SearchInputRefContext = createContext<React.Ref<HTMLInputElement>>(null);

function AddressSearchInput(props: InputProps) {
  const validationRef = useContext(SearchInputRefContext);
  const t = useTranslation("_common");
  const isTablet = useMediaQuery("(min-width: 768px)");

  return (
    <div>
      <Input
        {...props}
        onChange={handleSearchChange(props.onChange)}
        ref={mergeRefs(validationRef, props.ref) as React.Ref<HTMLInputElement>}
        placeholder={
          t(
            isTablet
              ? "address-autocomplete-placeholder"
              : "address-autocomplete-placeholder-sort",
          ) as string
        }
        startAdornment={
          <InputAdornment position="start">
            <Search size={20} strokeWidth={1.75} />
          </InputAdornment>
        }
      />
    </div>
  );
}

function normalizeLines(lines: string[]): string {
  return lines.filter((line) => line.trim() !== "").join(", ");
}

const emptyAddress: AddressFields = {
  "address-line-1": "",
  "address-line-2": "",
  city: "",
  country: "",
  region: "",
  "postal-code": "",
};

type AddressFieldConfig = {
  name: string;
  serverErrorPath?: string;
  errorKey: string;
  label: string;
  isRequired: boolean;
  type?: "input" | "select";
};

type RenderFieldProps<T extends HTMLInputElement | HTMLSelectElement> = {
  value: string;
  onChange: (value: string, ev: React.ChangeEvent<T>) => void;
  isValid: boolean | null;
  errorMessage: string | null;
  onBlur: () => void;
  ref: React.Ref<T>;
  name: string;
  label: string;
  required: boolean;
};

export function AddressAutocomplete(props: AddressAutocompleteProps) {
  const {
    label,
    name,
    fieldsConfig,
    initialScreen = "search",
    helpText,
    footerForm,
    backButtonLabel,
  } = props;

  const [screen, setScreen] =
    useState<AddressAutoCompleteScreens>(initialScreen);

  const t = useTranslation("_common");
  const setFieldValue = useDonationFormStore((state) => state.setFieldValue);
  const { isValidated, registerValidation } = useValidationGroup(name);
  const [showAutocompleteAddressError, setShowAutocompleteAddressError] =
    useState(true);
  const [autocompleteFailure, setAutocompleteFailure] =
    useState<AddressSearchFailure | null>(null);
  const autocompleteFailed = autocompleteFailure !== null;

  const autocompleteLabel =
    label || (t("address-autocomplete-label") as string);
  const autocompleteHelpText =
    helpText || (t("address-autocomplete-help-text") as string);
  // Only an error Loqate answered with, or a lost request, means the search
  // service is down. Anything else must not tell the donor that it is.
  const autocompleteErrorMessage = t(
    autocompleteFailure === "render"
      ? "address-autocomplete-crashed"
      : "address-autocomplete-error",
  ) as string;

  const someAddressFieldIsRequired = Object.values(fieldsConfig).some(
    (config) => config.isRequired,
  );
  const serverErrorPaths = Object.values(fieldsConfig)
    .map((config) => config.serverErrorPath)
    .filter((path): path is string => Boolean(path));

  useEffect(() => {
    if (screen !== "search") return;

    return registerValidation(`${name}-reveal-required-error`, {
      validate: () => {
        setShowAutocompleteAddressError(true);
        return { isValid: true };
      },
    });
  }, [screen, name, registerValidation]);

  const handleBackToSearch = () => {
    if (isValidated) {
      setShowAutocompleteAddressError(false);
    }
    setAutocompleteFailure(null);
    setFieldValue(name, emptyAddress);
    setScreen("search");
  };

  const addressFormFields: AddressFieldConfig[] = [
    {
      name: fieldsConfig["address-line-1"].name,
      serverErrorPath: fieldsConfig["address-line-1"].serverErrorPath,
      label: fieldsConfig["address-line-1"].label,
      errorKey: "address-line-1-required",
      isRequired: fieldsConfig["address-line-1"].isRequired || false,
    },
    {
      name: fieldsConfig["address-line-2"].name,
      serverErrorPath: fieldsConfig["address-line-2"].serverErrorPath,
      label: fieldsConfig["address-line-2"].label,
      errorKey: "address-line-2-required",
      isRequired: fieldsConfig["address-line-2"].isRequired || false,
    },
    {
      name: fieldsConfig.city.name,
      serverErrorPath: fieldsConfig.city.serverErrorPath,
      label: fieldsConfig.city.label,
      errorKey: "city-required",
      isRequired: fieldsConfig.city.isRequired || false,
    },
    {
      name: fieldsConfig.region.name,
      serverErrorPath: fieldsConfig.region.serverErrorPath,
      label: fieldsConfig.region.label,
      errorKey: "region-required",
      isRequired: fieldsConfig.region?.isRequired || false,
    },
    {
      name: fieldsConfig["postal-code"].name,
      serverErrorPath: fieldsConfig["postal-code"].serverErrorPath,
      label: fieldsConfig["postal-code"].label,
      errorKey: "postal-code-required",
      isRequired: fieldsConfig["postal-code"].isRequired || false,
    },
    {
      name: fieldsConfig.country.name,
      serverErrorPath: fieldsConfig.country.serverErrorPath,
      errorKey: "country-required",
      label: fieldsConfig.country.label,
      isRequired: fieldsConfig.country.isRequired || false,
      type: "select",
    },
  ];

  const renderInputField = ({
    value,
    onChange,
    isValid,
    errorMessage,
    onBlur,
    ref,
    name,
    label,
    required,
  }: RenderFieldProps<HTMLInputElement>) => (
    <FormControl
      label={label}
      error={errorMessage}
      isValid={isValid}
      required={required}
    >
      <Input
        name={name}
        value={value}
        onChange={(ev) => onChange(ev.target.value, ev)}
        onBlur={() => onBlur()}
        ref={ref}
      />
    </FormControl>
  );

  const renderSelectField = ({
    value,
    onChange,
    isValid,
    errorMessage,
    onBlur,
    ref,
    name,
    label,
    required,
  }: RenderFieldProps<HTMLSelectElement>) => (
    <FormControl
      label={label}
      error={errorMessage}
      isValid={isValid}
      required={required}
    >
      <Select
        name={name}
        value={value}
        onChange={(ev) => onChange(ev.target.value, ev)}
        onBlur={() => onBlur()}
        ref={ref}
      >
        <option value=""></option>
        {countryNames.map((country) => (
          <option key={country} value={country}>
            {country}
          </option>
        ))}
      </Select>
    </FormControl>
  );

  const createValidationSchema =
    (fieldConfig: AddressFieldConfig) => (groupState: AddressFields) => {
      const countryValue = getValueByPath(
        groupState,
        fieldsConfig.country.name,
      );
      const country = typeof countryValue === "string" ? countryValue : "";
      const validatesUkPostcode =
        fieldConfig.name === fieldsConfig["postal-code"].name &&
        isUkCountry(country);

      let schema: z.ZodType<string> = fieldConfig.isRequired
        ? z.string().min(1, {
            message: t(`errors.${fieldConfig.errorKey}`) as string,
          })
        : z.string();

      if (validatesUkPostcode) {
        schema = schema.refine(
          (value) =>
            (!fieldConfig.isRequired && value === "") ||
            isValidPostcodeForCountry(value, country),
          { message: t("errors.postal-code-invalid") as string },
        );
      }

      return fieldConfig.isRequired || validatesUkPostcode ? schema : null;
    };

  const renderAddressField = (fieldConfig: AddressFieldConfig) => (
    <Field<AddressFields>
      serverErrorPaths={
        fieldConfig.serverErrorPath ? [fieldConfig.serverErrorPath] : undefined
      }
      name={fieldConfig.name}
      dependencies={
        fieldConfig.name === fieldsConfig["postal-code"].name
          ? [fieldsConfig.country.name]
          : undefined
      }
      validate={createValidationSchema(fieldConfig)}
      render={({
        value,
        handleChange,
        isValid,
        errorMessage,
        handleBlur,
        ref,
      }) => {
        if (fieldConfig.type === "select") {
          return renderSelectField({
            value,
            onChange: handleChange,
            isValid,
            errorMessage,
            onBlur: handleBlur,
            ref: ref as React.Ref<HTMLSelectElement>,
            name: fieldConfig.name,
            label: fieldConfig.label,
            required: fieldConfig.isRequired,
          });
        }

        return renderInputField({
          value,
          onChange: handleChange,
          isValid,
          errorMessage,
          onBlur: handleBlur,
          ref: ref as React.Ref<HTMLInputElement>,
          name: fieldConfig.name,
          label: fieldConfig.label,
          required: fieldConfig.isRequired,
        });
      }}
    />
  );

  if (screen === "form") {
    return (
      <div>
        {!autocompleteFailed && (
          <div className={classes.headerForm}>
            <button
              type="button"
              onClick={handleBackToSearch}
              className={classes.backButton}
            >
              {backButtonLabel || t("back-to-search")}{" "}
              <Search
                size={18}
                strokeWidth={1.75}
                className={classes.backIcon}
              />
            </button>
          </div>
        )}
        {autocompleteFailed && (
          <div className={classes.autocompleteError}>
            <Alert variant="error">{autocompleteErrorMessage}</Alert>
          </div>
        )}
        <div className={classes.form}>
          {addressFormFields.map(renderAddressField)}
        </div>

        <div className={classes.footerForm}>{footerForm}</div>
      </div>
    );
  }

  return (
    <Field<AddressFields>
      serverErrorPaths={serverErrorPaths}
      name={name}
      validate={() => {
        if (someAddressFieldIsRequired) {
          return z.any().refine(() => false, {
            message: t("address-autocomplete-required") as string,
          });
        }
        return null;
      }}
      render={({ errorMessage, ref }) => {
        return (
          <div>
            <FormControl
              label={autocompleteLabel}
              error={showAutocompleteAddressError ? errorMessage : null}
              required={someAddressFieldIsRequired}
            >
              <AddressAutocompleteErrorBoundary
                onError={(failure) => {
                  setAutocompleteFailure(failure);
                  setScreen("form");
                }}
              >
                <div className={classes.searchWrapper}>
                  <SearchInputRefContext.Provider
                    value={ref as React.Ref<HTMLInputElement>}
                  >
                    <LoqateAddressSearch
                      inline
                      locale={LOCALE}
                      apiKey={LOQATE_API_KEY}
                      onSelect={(selectedAddress: LoqateAddress) => {
                        const addressLine1 = normalizeLines([
                          selectedAddress.Line1,
                          selectedAddress.Line2,
                        ]);

                        const addressLine2 = normalizeLines([
                          selectedAddress.Line3,
                          selectedAddress.Line4,
                          selectedAddress.Line5,
                        ]);

                        const country = countryNames.includes(
                          selectedAddress.CountryName,
                        )
                          ? selectedAddress.CountryName
                          : "";

                        setFieldValue(name, {
                          "address-line-1": addressLine1,
                          "address-line-2": addressLine2,
                          country,
                          city: selectedAddress.City,
                          region: selectedAddress.ProvinceName,
                          "postal-code": normalizePostcodeForCountry(
                            selectedAddress.PostalCode,
                            country,
                          ),
                        });

                        setScreen("form");
                      }}
                      countries={CONTRIES}
                      classes={{
                        list: classes.locateList,
                        listItem: classes.locateItem,
                      }}
                      components={{
                        List: InlineList,
                        ListItem: AddressSuggestion,
                        Input: AddressSearchInput,
                      }}
                    />
                  </SearchInputRefContext.Provider>
                </div>
              </AddressAutocompleteErrorBoundary>
            </FormControl>
            <div className={classes.helpText}>
              {autocompleteHelpText}{" "}
              <button
                type="button"
                onClick={() => setScreen("form")}
                className={classes.manualEntryButton}
              >
                {t("enter-it-manually")}
              </button>
            </div>
          </div>
        );
      }}
    />
  );
}
